// This file contains all the types for the notifications and the project feed.
// The corresponding backend types for websocket messages can be found in
// `@freelancer/datastore/core/websocket/server-message-event-model.ts`.

export interface NotificationContestData {
  isTest: boolean;
  jobString: string;
  linkUrl: string;
  title: string;
  userId: number;
  username: string;
}

export interface NotificationProjectData {
  appendedDescr: string;
  imgUrl: string;
  isTest: boolean;
  jobs: number[];
  jobString: string;
  linkUrl: string;
  showBudgetMinimallyPostedProject: boolean;
  text: string;
  time: number;
  title: string;
  userId: number;
  username: string;
}

export interface NotificationArticleComment {
  type: 'articleCommentReceived';
  parent_type: 'notifications';
  id: string;
  data: {
    articleAuthor: number; // The ID of author
    articleName: string;
    articleType: string; // Some enum?
    commentBy: number;
    commentId: number;
    comment: string;
    imgUrl: string;
    linkUrl: string;
    publicName: string;
    time: number;
    user: string;
  };
}
export interface NotificationAward {
  type: 'award';
  parent_type: 'notifications';
  id: string;
  data: {
    imgUrl?: string;
    linkUrl?: string;
    time: number;
    title: string;
    username: string;
  };
}

export interface NotificationAwardBadge {
  type: 'awardBadge';
  parent_type: 'notifications';
  id: string;
  data: {
    descr: string;
    imgUrl: undefined;
    linkUrl: string;
    name: string;
    time: number;
  };
}

export interface NotificationAwardCredit {
  type: 'awardCredit';
  parent_type: 'notifications';
  id: string;
  data: {
    amount: number;
    imgUrl: undefined;
    linkUrl: string;
    time: number;
  };
}

export interface NotificationAwardMilestoneReminder {
  type: 'awardMilestoneReminder';
  parent_type: 'notifications';
  id: string;
  data: {
    appendedDescr: string;
    currencyCode: string;
    currencyId: string | number;
    currencySign: string;
    imgUrl: string;
    linkUrl: string;
    publicName?: string;
    time: number;
    title: string;
    userId: number;
    username: string;
  };
}

export interface NotificationAwardReminder {
  type: 'awardReminder';
  parent_type: 'notifications';
  id: string;
  data: {
    name: string;
    time: number;
    username: string;
    linkUrl: string;
    imgUrl: string;
  };
}

export interface NotificationAwardXp {
  type: 'awardXp';
  parent_type: 'notifications';
  id: string;
  data: {
    amount: number;
    descr: string;
    imgUrl: undefined;
    linkUrl: string;
    time: number;
  };
}

export interface NotificationBid {
  type: 'bid';
  parent_type: 'notifications';
  id: string;
  data: {
    amount: string;
    bidAvg?: number;
    bidCount?: number;
    currency: string;
    imgUrl: string;
    linkUrl?: string;
    projName: string;
    publicName?: string;
    time: number;
    title: string;
    userId: number;
    username: string;
  };
}

export interface NotificationContest {
  type: 'contest';
  parent_type: 'notifications';
  id: string;
  data: NotificationContestData & {
    buyer: string;
    buyerUrl?: string;
    currency?: string;
    currencyCode: string;
    exchangeRate: string;
    imgUrl: string;
    jobs: number[];
    minBudget?: number;
    maxBudget?: number;
    time: number;
  };
}

export interface NotificationContestAwardedToEmployer {
  type: 'contestAwardedToEmployer';
  parent_type: 'notifications';
  id: string;
  data: {
    contestName: string;
    contestSeoUrl?: string;
    currencyCode: string;
    currencySign: string;
    entryId: string;
    entryNumber: string;
    entryCount: string;
    freelancerCount: string;
    imgUrl: string;
    isBought: boolean;
    linkUrl: string;
    prize: string | number;
    publicName?: string;
    thumb: string;
    time: number;
    userId: number;
    username: string;
  };
}

export interface NotificationContestAwardedToFreelancer {
  type: 'contestAwardedToFreelancer';
  parent_type: 'notifications';
  id: string;
  data: {
    contestName: string;
    contestSeoUrl: string;
    currencyCode: string;
    currencySign: string;
    entryId: string | number;
    entryNumber: string | number;
    entryCount: string | number;
    freelancerCount: string | number;
    imgUrl: string;
    linkUrl: string;
    prize: string | number;
    publicName?: string;
    time: number;
    userId: string | number;
    username: string;
  };
}

export interface NotificationContestEntryBoughtToEmployer {
  type: 'contestEntryBoughtToEmployer';
  parent_type: 'notifications';
  id: string;
  data: {
    contestName: string;
    entryId: string | number;
    entryNumber: string | number;
    imgUrl: string;
    linkUrl: string;
    publicName?: string;
    time: number;
    username: string;
  };
}

export interface NotificationContestEntryBoughtToFreelancer {
  type: 'contestEntryBoughtToFreelancer';
  parent_type: 'notifications';
  id: string;
  data: {
    contestName: string;
    entryId: string | number;
    entryNumber: string | number;
    imgUrl: string;
    linkUrl: string;
    publicName?: string;
    time: number;
    username: string;
  };
}

export interface NotificationContestEntry {
  type: 'contestEntry';
  parent_type: 'notifications';
  id: string;
  data: {
    contestHolder: string;
    contestId: string;
    contestName: string;
    firstUser?: string;
    firstUserPublicName?: string;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string;
    secondUserPublicName?: string;
    time: number;
    userId: number;
  };
}

export interface NotificationContestEntryRatedToFreelancer {
  type: 'contestEntryRatedToFreelancer';
  parent_type: 'notifications';
  id: string;
  data: {
    action: string;
    contestId: number;
    contestName: string;
    employerId: number;
    entriesUrl: string;
    entryId: number;
    entryNumber: number;
    entryS3path: string;
    entryThumb: string;
    entryUrl: string;
    freelancerId: number;
    hasOtherEntriesRatedOrRejected: boolean;
    imgUrl: string;
    linkUrl: string;
    ratedOrRejectedEntriesCount: number;
    rating: string;
    time: number;
  };
}

export interface NotificationContestEntryRejectedToFreelancer {
  type: 'contestEntryRejectedToFreelancer';
  parent_type: 'notifications';
  id: string;
  data: {
    action: string;
    contestId: number;
    contestName: string;
    employerId: number;
    entriesUrl: string;
    entryId: number;
    entryNumber: number;
    entryS3path: string;
    entryThumb: string;
    entryUrl: string;
    freelancerId: number;
    hasOtherEntriesRatedOrRejected: boolean;
    imgUrl: string;
    linkUrl: string;
    ratedOrRejectedEntriesCount: number;
    rating: string;
    time: number;
  };
}

export interface NotificationContestEntryReconsideredToFreelancer {
  type: 'contestEntryReconsideredToFreelancer';
  parent_type: 'notifications';
  id: string;
  data: {
    action: string;
    contestId: number;
    contestName: string;
    employerId: number;
    entriesUrl: string;
    entryId: number;
    entryNumber: number;
    entryS3path: string;
    entryThumb: string;
    entryUrl: string;
    freelancerId: number;
    hasOtherEntriesRatedOrRejected: boolean;
    imgUrl: string;
    linkUrl: string;
    ratedOrRejectedEntriesCount: number;
    rating: string;
    time: number;
  };
}

export interface NotificationContestEntryCommentedToFreelancer {
  type: 'contestEntryCommentedToFreelancer';
  parent_type: 'notifications';
  id: string;
  data: {
    contestId: number;
    contestName: string;
    entryNumber: number;
    imgUrl: string;
    linkUrl: string;
    time: number;
  };
}

export interface NotificationContestPCBNotification {
  type: 'contestPCBNotification';
  parent_type: 'notifications';
  id: string;
  data: {
    contestHolder: string;
    contestId: string;
    contestName: string;
    firstUser?: string;
    firstUserPublicName?: string;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string;
    secondUserPublicName?: string;
    time: number;
    userId: number;
  };
}

export interface NotificationContestPCBNotificationFullView {
  type: 'contestPCBNotificationFullView';
  parent_type: 'notifications';
  id: string;
  data: {
    contestHolder: string | number;
    contestName: string;
    entryNumber?: string | number;
    firstUser: string;
    firstUserPublicName?: string;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string;
    secondUserPublicName?: string;
    time: number;
    userId: string | number;
  };
}

export interface NotificationCreateMilestone {
  type: 'createMilestone';
  parent_type: 'notifications';
  id: string;
  data: NotificationProjectData & {
    accountId?: number;
    amount: string | number;
    bidId: string | number;
    currencyCode: string;
    currencySign: string;
    projectId: string | number;
    publicName?: string;
    shortDescr: string;
    time: number;
    title: string;
    transId: string | number;
  };
}

export interface NotificationDenyed {
  type: 'denyed';
  parent_type: 'notifications';
  id: string;
  data: {
    imgUrl: string;
    jobsURL: string;
    linkUrl: string;
    name: string;
    publicName?: string;
    seoUrl: string;
    time: number;
    userId: string | number;
    username: string;
  };
}

export interface NotificationDraftContest {
  type: 'draftContest';
  parent_type: 'notifications';
  id: string;
  data: {
    contestEditUrl?: string;
    contestId: string;
    contestName: string;
    linkUrl: string;
    imgUrl: string;
    isPublished: boolean;
    time: number;
  };
}

export interface NotificationFailingProject {
  type: 'failingProject';
  parent_type: 'notifications';
  id: string;
  data: NotificationProjectData & {
    address?: string;
    currency?: string;
    currencyCode: string;
    featured: boolean;
    freeBidUntil?: number;
    fulltime: boolean;
    maxBudget?: number;
    minBudget?: number;
    nonpublic: boolean;
    projIsHourly: boolean;
    publicName?: string;
    submitDate: string;
  };
}

export interface NotificationHireMe {
  type: 'hireMe';
  parent_type: 'notifications';
  id: string;
  data: {
    currency?: { code: string; sign: string };
    imgUrl: string;
    linkUrl: string;
    period?: number;
    projIsHourly: boolean;
    publicName?: string;
    username: string;
    time: number;
    title: string;
    sum?: number;
  };
}

export interface NotificationNotifyFollower {
  type: 'notifyfollower';
  parent_type: 'notifications';
  id: string;
  data: NotificationProjectData;
}

export interface NotificationHighLtvAnnualDiscount {
  type: 'highLtvAnnualDiscount';
  parent_type: 'notifications';
  id: string;
  data: {
    imgUrl: undefined;
    linkUrl: string;
    time: number;
    userDisplayName: string;
  };
}

export interface NotificationInviteUserBid {
  type: 'inviteUserBid';
  parent_type: 'notifications';
  id: string;
  data: NotificationProjectData & {
    currencyCode: string;
    currencySign: string;
    inviteeId: number;
    inviteeName: string;
    maxBudget?: number;
    minBudget?: number;
    publicName?: string;
  };
}

export interface NotificationInviteToContest {
  type: 'inviteToContest';
  parent_type: 'notifications';
  id: string;
  data: {
    buyer: string | number; // The ID
    buyerName: string;
    buyerPublicName?: string;
    buyerUrl: string;
    contestRecommendedSkills: string;
    currencyCode: string;
    currencySign: string;
    description: string;
    imgUrl: string;
    linkUrl: string;
    name: string;
    prize: string | number;
    sellerId: string | number;
    sellerName: string;
    sellerPublicName?: string;
    time: number;
    title: string;
    type?: string;
  };
}

export interface NotificationInvoiceRequested {
  type: 'invoiceRequested';
  parent_type: 'notifications';
  id: string;
  data: {
    accountId: number;
    amount: number;
    bidId: number;
    currencyCode: string;
    currencySign: string;
    imgUrl: string;
    invoiceId: number;
    linkUrl: string;
    name: string;
    publicName?: string;
    sellerId: number;
    seoUrl: string;
    time: number;
    userId: number;
    username: string;
  };
}

export interface NotificationLevelUp {
  type: 'levelUp';
  parent_type: 'notifications';
  id: string;
  data: {
    imgUrl: undefined;
    level: string;
    linkUrl: string;
    membership: boolean;
    perks: {
      description: string;
      item: boolean;
      magnitude: number;
      name?: 'BidRefreshRate' | 'item';
      symbol: string;
    }[];
    time: number;
    username: string;
  };
}

export interface NotificationLocalJobPosted {
  type: 'localJobPosted';
  parent_type: 'notifications';
  id: string;
  data: NotificationProjectData & {
    currency?: string;
    currencyCode: string;
    exchangeRate: string;
    featured: boolean;
    freeBidUntil?: number;
    fulltime: boolean;
    isLocal: true;
    maxBudget?: number;
    minBudget?: number;
    nonpublic: boolean;
    projIsHourly: boolean;
    publicName?: string;
    submitDate: string;
  };
}

export interface NotificationRecruiterProject {
  type: 'recruiterProject';
  parent_type: 'notifications';
  id: string;
  data: NotificationProjectData & {
    address?: string;
    currency?: string;
    currencyCode: string;
    exchangeRate: string;
    featured: boolean;
    freeBidUntil?: number;
    fulltime: boolean;
    isLocal: boolean;
    maxBudget?: number;
    minBudget?: number;
    nonpublic: boolean;
    projIsHourly: boolean;
    publicName?: string;
    submitDate: string;
  };
}

export interface NotificationProject {
  type: 'project';
  parent_type: 'notifications';
  id: string;
  data: NotificationProjectData & {
    address?: string;
    currency?: string;
    currencyCode: string;
    exchangeRate: string;
    featured: boolean;
    freeBidUntil?: number;
    fulltime: boolean;
    maxBudget?: number;
    minBudget?: number;
    nonpublic: boolean;
    projIsHourly: boolean;
    publicName?: string;
    submitDate: string;
    usdMaxbudget?: number;
    usdMinbudget?: number;
  };
}

export interface NotificationProjectCompleted {
  type: 'completed';
  parent_type: 'notifications';
  id: string;
  data: {
    amount: string | number;
    categoryName: string;
    completeStatus: string; // TODO: T37445 Make this a string enum
    currencyCode: string;
    currencyId: string | number;
    currencySign: string;
    employerId: string | number;
    employerPublicName?: string;
    employerUsername: string;
    expiredTimestamp: string;
    freelancerId: string | number;
    freelancerPublicName?: string;
    freelancerUsername: string;
    imgUrl: string;
    linkUrl: string;
    name: string;
    selectionId: string | number;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    submitDate: string;
    time: number;
  };
}

export interface NotificationQuickHireProject {
  type: 'quickHireProject';
  parent_type: 'notifications';
  id: string;
  data: NotificationProjectData & {
    currency?: { code: string; sign: string };
    period?: number;
    projIsHourly: boolean;
    publicName?: string;
    sum?: number;
  };
}

export interface NotificationReleaseMilestone {
  type: 'releaseMilestone';
  parent_type: 'notifications';
  id: string;
  data: {
    accountId: string | number;
    amount: string | number;
    currencyCode: string;
    currencySign: string;
    imgUrl: string;
    linkUrl: string;
    name: string;
    otherReason: string;
    publicName?: string;
    seoUrl: string;
    submitDate: string;
    time: number;
    title: string;
    userId: number;
    username: string;
  };
}

export interface NotificationRequestEndProject {
  type: 'requestEndProject';
  parent_type: 'notifications';
  id: string;
  data: {
    accountId: string | number;
    bidId: string | number;
    categoryName: string;
    dayCount: string | number;
    imgUrl: string;
    linkUrl: string;
    name: string;
    publicName?: string;
    seoUrl: string;
    submitDate: string;
    time: number;
    userId: string | number;
    username: string;
  };
}

export interface NotificationRequestMilestone {
  type: 'requestMilestone';
  parent_type: 'notifications';
  id: string;
  data: {
    accountId: number;
    amount: number;
    bidId: number;
    currencyCode: string;
    currencyId: number;
    currencySign: string;
    description: string;
    imgUrl: string;
    linkUrl: string;
    name: string;
    publicName?: string;
    requestId: number;
    sellerId: number;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    submitDate: string;
    time: number;
    userId: number;
    username: string;
  };
}

export interface NotificationRequestToRelease {
  type: 'requestToRelease';
  parent_type: 'notifications';
  id: string;
  data: {
    accountId: number;
    amount: number;
    bidId: number;
    currencyCode: string;
    currencySign: string;
    descr: string;
    // img: ???;
    imgUrl: string;
    linkUrl: string;
    name: string;
    publicName?: string;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    time: number;
    tranId: number;
    userId: number;
    username: string;
  };
}

export interface NotificationServiceProjectEndingSeller {
  type: 'serviceProjectEndingSeller';
  parent_type: 'notifications';
  id: string;
  data: {
    bidId: number;
    buyerId: number;
    buyerName: string;
    hoursRemaining: number;
    imgUrl: undefined;
    linkUrl: string;
    orderStartDate: string;
    projectId: number;
    projectName: string;
    projectUrl: string;
    sellerId: number;
    sellerName: string;
    serviceId: number;
    serviceName: string;
    time: number;
  };
}

export interface NotificationServiceProjectPostedSeller {
  type: 'serviceProjectPostedSeller';
  parent_type: 'notifications';
  id: string;
  data: {
    bidId: number;
    buyerId: number;
    buyerImgUrl: string;
    buyerName: string;
    buyerPublicName?: string;
    currencyCode: string;
    currencySign: string;
    imgUrl: string;
    linkUrl: string;
    orderEndDate: string;
    orderStartDate: string;
    projectCost: number;
    // project_duration: ???;
    projectId: number;
    // project_milestones: ???;
    projectName: string;
    projectUrl: string;
    sellerId: number;
    sellerImgUrl: string;
    sellerName: string;
    sellerPublicName: string;
    serviceId: number;
    serviceName: string;
    serviceUrl: string;
    time: number;
  };
}

export interface NotificationServiceData {
  imgUrl: undefined;
  linkUrl: string;
  serviceId: number;
  serviceName: string;
  serviceUrl: string;
  serviceDuration: string;
  serviceCost: number;
  currencySign: string;
  currencyCode: string;
  serviceImg: string; // A URL
  time: number;
}

export interface NotificationServiceApproved {
  type: 'serviceApproved';
  parent_type: 'notifications';
  id: string;
  data: NotificationServiceData;
}

export interface NotificationServiceRejected {
  type: 'serviceRejected';
  parent_type: 'notifications';
  id: string;
  data: NotificationServiceData;
}

export interface NotificationSignUpFreeTrialUpsell {
  type: 'signUpFreeTrialUpsell';
  parent_type: 'notifications';
  id: string;
  data: {
    imgUrl: undefined;
    linkUrl: string;
    time: number;
    trialPackageName: string;
  };
}

export interface NotificationUpsellCrowdsourceFinding {
  type: 'upsellCrowdsourceFinding';
  parent_type: 'notifications';
  id: string;
  data: {
    imgUrl: undefined;
    linkUrl: undefined;
    projectName: string;
    time: number;
  };
}

export interface NotificationXpContest {
  type: 'xpContest';
  parent_type: 'notifications';
  id: string;
  data: NotificationContestData & {
    currency: string;
    currencyCode: string;
    imgUrl: string;
    minBudget?: number;
    maxBudget?: number;
    time: number;
  };
}

export type NotificationEntry =
  | NotificationArticleComment
  | NotificationAward & { state?: string } // TODO: T37445 Make this a string enum
  | NotificationAwardBadge
  | NotificationAwardCredit
  | NotificationAwardMilestoneReminder
  | NotificationAwardReminder
  | NotificationAwardXp
  | NotificationBid
  | NotificationContestAwardedToEmployer
  | NotificationContestAwardedToFreelancer
  | NotificationContestEntry
  | NotificationContestEntryBoughtToEmployer
  | NotificationContestEntryBoughtToFreelancer
  | NotificationContestEntryCommentedToFreelancer
  | NotificationContestEntryRatedToFreelancer
  | NotificationContestEntryRejectedToFreelancer
  | NotificationContestEntryReconsideredToFreelancer
  | NotificationContestPCBNotification
  | NotificationContestPCBNotificationFullView
  | NotificationCreateMilestone
  | NotificationDenyed
  | NotificationDraftContest
  | NotificationHighLtvAnnualDiscount
  | NotificationHireMe
  | NotificationInviteToContest
  | NotificationInviteUserBid
  | NotificationInvoiceRequested
  | NotificationLevelUp
  | NotificationNotifyFollower
  | NotificationProjectCompleted
  | NotificationQuickHireProject
  | NotificationReleaseMilestone
  | NotificationRequestEndProject
  | NotificationRequestMilestone
  | NotificationRequestToRelease
  | NotificationServiceApproved
  | NotificationServiceProjectEndingSeller
  | NotificationServiceProjectPostedSeller
  | NotificationServiceRejected
  | NotificationSignUpFreeTrialUpsell
  | NotificationUpsellCrowdsourceFinding;

/** There are certain newsfeed notifications that we want to ignore,
 * so we map them to `NotificationEntrySkipped`.
 * We have to map them to something otherwise asking for 20 might only return
 * 15 which would break pagination.
 */
export interface NotificationEntrySkipped {
  type: undefined;
  parent_type: 'notifications';
  id: string;
}

export function isNotificationEntry(
  notification: NotificationEntry | NotificationEntrySkipped,
): notification is NotificationEntry {
  return notification.type !== undefined;
}



// WEBPACK FOOTER //
// ./src/@freelancer/datastore/notifications/notifications.model.ts